import { forwardRef, useImperativeHandle, useCallback } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import CodeMirror from '@uiw/react-codemirror'
import { python } from '@codemirror/lang-python'
import { oneDark } from '@codemirror/theme-one-dark'
import type { z } from 'zod'

import { codeConfigSchema } from '@/schemas/workflow-node'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

type CodeConfig = z.infer<typeof codeConfigSchema>

export interface NodeFormProps {
  defaultValues: Record<string, unknown>
  onSubmit: (data: Record<string, unknown>) => void
  disabled?: boolean
}

export interface NodeFormHandle {
  submit: () => void
}

const pythonExtensions = [python(), oneDark]

export const CodeNodeForm = forwardRef<NodeFormHandle, NodeFormProps>(
  ({ defaultValues, onSubmit, disabled }, ref) => {
    const { t } = useTranslation('workflows')

    const {
      register,
      handleSubmit,
      control,
      formState: { errors },
    } = useForm<CodeConfig>({
      resolver: zodResolver(codeConfigSchema),
      defaultValues: {
        language: 'python',
        ...defaultValues,
      } as CodeConfig,
    })

    useImperativeHandle(ref, () => ({
      submit: () => {
        handleSubmit((data) => onSubmit(data as Record<string, unknown>))()
      },
    }))

    const onCodeChange = useCallback(
      (onChange: (value: string) => void) => (value: string) => {
        onChange(value)
      },
      []
    )

    return (
      <form
        className="space-y-4"
        onSubmit={handleSubmit((data) => onSubmit(data as Record<string, unknown>))}
      >
        {/* Language */}
        <div className="space-y-2">
          <Label htmlFor="language">Language</Label>
          <Select value="python" disabled>
            <SelectTrigger className="bg-white/5 border-gray-700/50">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="python">Python</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-sm text-muted-foreground">
            {t('flow.helperCodeLanguage')}
          </p>
        </div>

        {/* Source Code */}
        <div className="space-y-2">
          <Label htmlFor="source">Source</Label>
          <Controller
            name="source"
            control={control}
            render={({ field }) => (
              <CodeMirror
                value={field.value}
                onChange={onCodeChange(field.onChange)}
                extensions={pythonExtensions}
                editable={!disabled}
                minHeight="200px"
                className="rounded-md border border-gray-700/50 overflow-hidden"
              />
            )}
          />
          {errors.source && (
            <p className="text-sm text-destructive">{t('flow.requiredField')}</p>
          )}
        </div>

        {/* Timeout */}
        <div className="space-y-2">
          <Label htmlFor="timeout_seconds">Timeout (seconds)</Label>
          <Input
            id="timeout_seconds"
            type="number"
            className="bg-white/5 border-gray-700/50 focus:border-indigo-500/50"
            disabled={disabled}
            {...register('timeout_seconds', { valueAsNumber: true })}
          />
          {errors.timeout_seconds && (
            <p className="text-sm text-destructive">{errors.timeout_seconds.message}</p>
          )}
        </div>
      </form>
    )
  }
)

CodeNodeForm.displayName = 'CodeNodeForm'
